import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DocumentText } from "iconsax-react";
import { memo } from "react";
import { useTranslation } from "react-i18next";

import { CommissionInvoiceStatus } from "@/api/schemas/commissionInvoiceStatus";
import { StatusBadge } from "@/components/Atoms";
import type { BillingInvoicesListProps } from "./BillingInvoicesList.types";
import * as styles from "./BillingInvoicesList.styles";

export type { BillingInvoicesListProps } from "./BillingInvoicesList.types";

const INVOICE_STATUS_MAP = {
  [CommissionInvoiceStatus.paid]: { labelKey: "invoices.paid", color: "success" as const },
  [CommissionInvoiceStatus.pending]: { labelKey: "invoices.pending", color: "warning" as const },
  [CommissionInvoiceStatus.failed]: { labelKey: "invoices.failed", color: "error" as const },
  [CommissionInvoiceStatus.canceled]: { labelKey: "invoices.canceled", color: "neutral" as const },
};

export const BillingInvoicesList = memo(function BillingInvoicesList({
  invoices,
}: BillingInvoicesListProps) {
  const { t } = useTranslation("billing");
  const theme = useTheme();

  if (invoices.length === 0) return null;

  return (
    <Box sx={styles.card}>
      <Typography component="h3" sx={styles.title}>
        {t("invoices.title")}
      </Typography>

      {invoices.map((invoice) => {
        const statusDisplay =
          INVOICE_STATUS_MAP[invoice.status] ?? INVOICE_STATUS_MAP[CommissionInvoiceStatus.pending];

        return (
          <Box key={invoice.id}>
            <Box sx={styles.desktopRow}>
              <Box sx={styles.desktopLeft}>
                <DocumentText size={16} variant="Bold" color={theme.palette.text.secondary} />
                <Typography component="span">{invoice.shopName}</Typography>
                <Typography component="span" sx={styles.secondaryText}>
                  {invoice.date}
                </Typography>
              </Box>
              <Box sx={styles.desktopRight}>
                <Typography sx={styles.desktopAmount}>
                  {invoice.amount} {t("stats.currency")}
                </Typography>
                <StatusBadge color={statusDisplay.color} size="sm" label={t(statusDisplay.labelKey)} />
              </Box>
            </Box>

            <Box sx={styles.mobileRow}>
              <Box sx={styles.mobileTop}>
                <Typography sx={styles.mobileShopName}>{invoice.shopName}</Typography>
                <Typography sx={styles.mobileAmount}>
                  {invoice.amount} {t("stats.currency")}
                </Typography>
              </Box>
              <Typography sx={styles.mobileDate}>{invoice.date}</Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
});
